import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ScrollerModule } from 'primeng/scroller';
import { TableModule } from 'primeng/table';
import { ChartOptions, CustomChartReport } from 'src/app/interfaces/custom-chart-report/custom-chart-report.interface';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { PrintGroupPaginatorService } from 'src/app/services/global-services/print-group-paginator.service';

@Component({
  selector: 'app-custom-table-report',
  templateUrl: './custom-table-report.component.html',
  styleUrls: ['./custom-table-report.component.scss'],
  standalone: true,
  imports: [CommonModule, ButtonModule, TableModule, ScrollerModule, DropdownModule, FormsModule, NgApexchartsModule],
  providers: [ConfirmationService]
})
export class CustomTableReportComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() dataReport: CustomTableReport;
  @Input() daysMark: number = 0;
  @Input() extraMargin: number = 60;
  @Input() filtersHeight: number = 0;
  @Input() footerTemplate?: TemplateRef<any>;
  @Input() customChartReport: CustomChartReport;
  @Input() showChartsBarVertical: boolean = false;
  @Input() showChartsLineChart: boolean = false;
  @Input() showSeparators: boolean = false;
  @Input() virtualScroll: boolean = false;
  @Input() virtualScrollItemSize: number = 26;
  @ViewChild('headerContainer') headerContainer!: ElementRef;
  @ViewChild('chartsContainerBarVertical') chartsContainerBarVertical!: ElementRef;
  @ViewChild('chartsContainerLineChart') chartsContainerLineChart!: ElementRef;

  @Input() printBreakDepth: number = -1;

  barOptions: Partial<ChartOptions>;
  lineOptions: Partial<ChartOptions>;
  resizeObserver?: ResizeObserver;
  scrollHeight: string = '240px';
  tableData: any[] = [];
  view: any;

  constructor(private cdr: ChangeDetectorRef,
    private printGroupPaginatorService: PrintGroupPaginatorService,
    private el: ElementRef
  ) { }

  @HostBinding('class.virtual-scroll')
  get isVirtualScroll(): boolean {
    return this.virtualScroll;
  }

  ngOnInit() {
    this.refreshTableData();
    this.scrollHeight = `${Math.max(window.innerHeight - this.extraMargin, 240)}px`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataReport'] || changes['virtualScroll']) {
      this.refreshTableData();
    }
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateScrollHeight();
    this.resizeCharts();
  }

  @HostListener('window:beforeprint')
  onBeforePrint() {
    if (this.printBreakDepth < 0) return;
    this.printGroupPaginatorService.restructure(this.el.nativeElement, this.printBreakDepth);
  }

  @HostListener('window:afterprint')
  onAfterPrint() {
    if (this.printBreakDepth < 0) return;
    this.printGroupPaginatorService.revert(this.el.nativeElement);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  alignColumnText(value: any): string {
    if (this.isDecimal(value)) return 'right';

    // Si tiene el formato de fecha, centrar
    if (this.isValidDate(value)) return 'center';

    // Si el valor es un número pero contiene espacios, alinear a la izquierda
    if (typeof value === 'string') return 'left';

    if (isNaN(Number(value))) return 'left';

    return 'center'
  }

  private initResizeObserver() {
    if (this.headerContainer?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollHeight();
      });
      this.resizeObserver.observe(this.headerContainer.nativeElement);
    }
    this.resizeCharts();
  }

  private flattenRows(rows: any[] | undefined, depth: number = 0): any[] {
    if (!rows?.length) return [];
    const flattened: any[] = [];
    for (const row of rows) {
      flattened.push({ ...row, children: undefined, _depth: depth });
      if (row.children?.length) {
        flattened.push(...this.flattenRows(row.children, depth + 1));
      }
    }
    return flattened;
  }

  getBackgroundColor(depth: number): string {
    const startColor = [200, 200, 200];
    const endColor = [244, 244, 244];

    const r = Math.round(startColor[0] + (endColor[0] - startColor[0]) * (depth / 3));
    const g = Math.round(startColor[1] + (endColor[1] - startColor[1]) * (depth / 3));
    const b = Math.round(startColor[2] + (endColor[2] - startColor[2]) * (depth / 3));

    return `rgb(${r}, ${g}, ${b})`;
  }

  isDecimal(value: any): boolean {
    const includesDot = value?.toString()?.includes('.');

    if (!includesDot) return false;
    if (isNaN(Number(value))) return false;

    return true;
  }

  isValidDate(value: any): boolean {
    if (!value || typeof value !== 'string') return false;

    return moment(value, ['DD/MM/YYYY', 'DD-MM-YYYY'], true).isValid();
  }

  private loadCharts() {
    if (!this.customChartReport?.result) return;

    if (!this.showChartsBarVertical && !this.showChartsLineChart) return;

    if (this.showChartsBarVertical) {
      const transformed = this.transformBarChartData(this.customChartReport.result);

      // 📊 BAR CHART
      this.barOptions = {
        series: transformed!.series,
        chart: {
          type: 'bar' as const,
          width: this.view[0],
          height: this.view[1],
          toolbar: {
            tools: {
              download: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false
          }
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: transformed!.categories,
          title: {
            text: this.customChartReport.xAxisLabel
          }
        },
        yaxis: {
          title: {
            text: this.customChartReport.yAxisLabel
          }
        }
      };
    }

    if (this.showChartsLineChart) {
      const transformed = this.transformLineChartData(this.customChartReport.result);

      // 📈 LINE CHART
      this.lineOptions = {
        series: transformed!.series,
        chart: {
          type: 'line' as const,
          width: this.view[0],
          height: this.view[1],
          toolbar: {
            tools: {
              download: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: false
          }
        },
        markers: {
          size: 5,
          strokeWidth: 2,
          hover: {
            size: 7
          }
        },
        xaxis: {
          categories: transformed!.categories,
          title: {
            text: this.customChartReport.xAxisLabel
          }
        },
        yaxis: {
          title: {
            text: this.customChartReport.yAxisLabel
          }
        }
      };
    }
  }

  private refreshTableData() {
    const source = this.dataReport?.data ?? [];
    this.tableData = this.virtualScroll ? this.flattenRows(source) : source;
  }

  resizeCharts() {
    setTimeout(() => {
      const width = this.headerContainer?.nativeElement?.offsetWidth || 900;
      this.view = [width - 80, 300];
      this.loadCharts();
    }, 100);
  }

  private transformLineChartData(data: any[]) {
    if (!data || data.length === 0) return null;

    // 1. Obtener TODOS los meses únicos
    const categoriesSet = new Set<number>();

    data.forEach(year => {
      year.series.forEach((s: { name: any; }) => {
        categoriesSet.add(Number(s.name));
      });
    });

    // 2. Ordenarlos correctamente
    const categories = Array.from(categoriesSet).sort((a, b) => a - b);

    // 3. Construir series alineadas
    const series = data.map(year => ({
      name: year.name.toString(),
      data: categories.map(month => {
        const found = year.series.find((s: { name: any; }) => Number(s.name) === month);
        return found ? found.value : null;
      })
    }));

    return { series, categories };
    //   if (!data || data.length === 0) return null;

    //   const categories = data[0].series.map((s: { name: any; }) => s.name);

    //   const series = data.map(item => ({
    //     name: item.name.toString(),
    //     data: item.series.map((s: { value: any; }) => s.value ?? 0)
    //   }));

    //   return { series, categories };
  }

  private transformBarChartData(data: any[]) {
    return {
      series: [{
        name: 'Valor',
        data: data.map(x => x.value)
      }],
      categories: data.map(x => x.name)
    };
  }



  private updateScrollHeight() {
    const headerHeight = this.headerContainer?.nativeElement?.offsetHeight || 0;
    this.scrollHeight = `${window.innerHeight - headerHeight - this.extraMargin}px`;
    this.cdr.detectChanges();
  }


  validateScrollHeight(): boolean {
    if (this.showChartsBarVertical || this.showChartsLineChart) {
      return false;
    }
    return true;
  }
}
